import { notifyError } from '@/bugsnag';
import type { Country } from '@ds/form/input/DsPhoneInput/phone-input.types';
import parsePhoneNumber, {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    isValidPhoneNumber,
    type CountryCode,
} from 'libphonenumber-js';

let displayNamesInstance: Intl.DisplayNames;
let displayNamesLocale: string = 'en';

export enum PhoneNumberFormat {
    NATIONAL = 'NATIONAL',
    INTERNATIONAL = 'INTERNATIONAL',
    E164 = 'E164',
}

export const US_PHONE_NUMBER_VALIDATION_ERROR =
    'Please enter a valid US phone number (e.g., +1234567890, 2345678901, or (234) 567-8901)';

export const updateUSPhoneNumber = (phoneNumber: string): string | null => {
    const formattedPhone = formatPhoneNumber(phoneNumber, 'US', PhoneNumberFormat.NATIONAL);

    if (!isValidUSPhoneNumber(formattedPhone)) return null;

    return formattedPhone;
};

export const isValidUSPhoneNumber = (phoneNumber: string): boolean => {
    return !!phoneNumber && isValidPhoneNumber(phoneNumber, 'US');
};

export const isValid = (phoneNumber: string, countryCode: CountryCode = 'US'): boolean => {
    return !!phoneNumber && isValidPhoneNumber(phoneNumber, countryCode);
};

export const formatPhoneNumber = (
    phoneNumber: string,
    countryCode: CountryCode = 'US',
    format: PhoneNumberFormat = PhoneNumberFormat.NATIONAL,
): string => {
    if (!phoneNumber) return phoneNumber;

    const parsedNumber = parsePhoneNumber(phoneNumber, countryCode);
    if (!parsedNumber) return phoneNumber;

    // If the phone number is invalid (wrong length), return as-is
    if (!parsedNumber.isValid()) return phoneNumber;

    switch (format) {
        case PhoneNumberFormat.NATIONAL:
            return parsedNumber.formatNational();
        case PhoneNumberFormat.INTERNATIONAL:
            return parsedNumber.formatInternational();
        case PhoneNumberFormat.E164:
            return parsedNumber.number;
        default:
            return phoneNumber;
    }
};

export const convertToBackendFormat = (phoneNumber: string, countryCode: CountryCode = 'US'): string => {
    if (!phoneNumber) return phoneNumber;

    const formatted = formatPhoneNumber(phoneNumber, countryCode, PhoneNumberFormat.E164);
    return formatted;
};

export const convertBackendToDisplayFormat = (phoneNumber: string, countryCode: CountryCode = 'US'): string => {
    if (!phoneNumber) return 'Not set';

    if (phoneNumber.includes('(') && phoneNumber.includes(')')) {
        return phoneNumber;
    }

    const formatted = formatPhoneNumber(phoneNumber, countryCode, PhoneNumberFormat.NATIONAL);
    return formatted;
};

export const getCountryName = (
    locale: string,
    code: CountryCode,
    customCountriesNameListByIsoCode?: Record<CountryCode, string>,
): string | undefined => {
    if (customCountriesNameListByIsoCode?.[code]) {
        return customCountriesNameListByIsoCode[code];
    }

    if (displayNamesLocale !== locale || !displayNamesInstance) {
        displayNamesLocale = locale;
        displayNamesInstance = new Intl.DisplayNames([locale], {
            type: 'region',
        });
    }

    return displayNamesInstance.of(code.toUpperCase());
};

export const getAllSupportedCountries = (locale: string = 'en'): Country[] => {
    try {
        const countryCodes = getCountries();

        return countryCodes.map(countryCode => {
            const dialCode = getCountryCallingCode(countryCode);

            const name = getCountryName(locale, countryCode) || countryCode;

            return {
                iso2: countryCode,
                name: name,
                dialCode: dialCode,
            };
        });
    } catch (error) {
        notifyError(error, 'phoneNumber:getAllSupportedCountries');
        console.warn(`Error getting countries: ${error}`);
        return [];
    }
};

export const getCountryFromIso2 = (iso2: CountryCode, locale: string = 'en'): Country => {
    try {
        const dialCode = getCountryCallingCode(iso2);
        const name = getCountryName(locale, iso2) || iso2;

        return {
            iso2,
            name,
            dialCode,
        };
    } catch (error) {
        console.warn(`Error getting country from ISO2 code ${iso2}:`, error);
        notifyError(error, 'phoneNumber:getCountryFromIso2', { iso2Code: iso2 });

        return {
            iso2: 'US',
            name: 'United States',
            dialCode: getCountryCallingCode('US'),
        };
    }
};

export const parseInternationalNumber = (phoneNumber: string): Country | null => {
    if (!phoneNumber?.startsWith('+')) {
        return null;
    }

    const parsedNumber = parsePhoneNumber(phoneNumber);
    const possibleCountries = parsedNumber?.getPossibleCountries();
    const firstMatch = possibleCountries?.[0];

    if (!firstMatch) {
        return null;
    }

    const dialCode = getCountryCallingCode(firstMatch);
    const name = getCountryName('en', firstMatch) || firstMatch;

    return {
        iso2: firstMatch,
        name: name,
        dialCode: dialCode,
    };
};

export const formatAsYouType = (input: string, countryCode: CountryCode = 'US'): string => {
    if (!input) return '';

    try {
        const formatter = new AsYouType(countryCode);
        return formatter.input(input);
    } catch (error) {
        console.warn('Error in formatAsYouType:', error);
        return input;
    }
};

export const parseAsYouType = (
    input: string,
    countryCode: CountryCode = 'US',
): { e164: string; isValid: boolean; formatted: string } => {
    if (!input) {
        return {
            e164: '',
            isValid: false,
            formatted: '',
        };
    }

    try {
        const formatter = new AsYouType(countryCode.toUpperCase() as CountryCode);
        const formatted = formatter.input(input);
        const parsedNumber = formatter.getNumber();

        if (parsedNumber?.number) {
            return {
                e164: parsedNumber.number,
                isValid: parsedNumber.isValid() || false,
                formatted: formatted,
            };
        }

        return {
            e164: input,
            isValid: false,
            formatted: formatted || input,
        };
    } catch (error) {
        console.warn('Error in parseAsYouType:', error);
        return {
            e164: input,
            isValid: false,
            formatted: input,
        };
    }
};

export const formatE164ToNational = (e164Number: string, countryCode?: CountryCode): string => {
    if (!e164Number) return '';

    try {
        if (e164Number.startsWith('+')) {
            const parsed = parsePhoneNumber(e164Number);
            if (parsed) {
                return parsed.formatNational();
            }
        }

        if (countryCode) {
            const parsed = parsePhoneNumber(e164Number, countryCode);
            if (parsed) {
                return parsed.formatNational();
            }
        }
    } catch (error) {
        console.warn('Error in formatE164ToNational:', error);
    }

    return e164Number;
};
